import Cookies from "js-cookie";
import { delay, throttle, uniq, uniqBy } from "lodash";
import moment from "moment-timezone";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../api";
import {
  CHATTING,
  CHUNK_SIZE,
  conversationType,
  eventBusType,
  MAX_CHUNK_LENGTH,
  MESSAGE_STATUS,
  role,
} from "../config/Constant";
import { useInfoUser } from "../store/UserStore";
import {
  extractUserIds,
  openNotificationError,
  openNotificationSuccess,
  processFileUrl,
  sanitizeMentions,
  sortMessageListDesc,
} from "../utils/Utils";
import { useMenuContext } from "./MenuContext";

import { chatEvents$, sendChat } from "../api/webSocketRx";
import { updateOrgList } from "../utils/OrganizationHelper";
import { compressImage } from "../utils/SendFile";
import { useAuthLayoutContext } from "./AuthLayoutContext";
import { useSendingContext } from "./global/SendingProvider";
import { uploadEvent$ } from "../api/eventBus";

const REACT_APP_TYPING_TIME = process.env.REACT_APP_TYPING_TIME || 4000;
const MAX_DIMENSION = 500;

const ChatContext = createContext(null);
export const useChatContext = () => {
  return useContext(ChatContext);
};

export const ChatProvider = ({ children }) => {
  /** Context */
  const {
    conversationList,
    handleUpdateConversationList,
    selectedConversation,
    setSelectedConversation,
    activeKey,
    setActiveKey,
    isMessageSearch,
    messageSearch,
    setIsMessageSearch,
    setPreviousScrollHeight,
    setMessageSearch,
    setIsMenu,
    setIsModalLabelGroup,
  } = useMenuContext();

  const { setOrgList: setOriginalOrgList, orgList: originalOrgList } =
    useAuthLayoutContext();
  const { selectedPersonalId } = useParams();
  const [searchParams] = useSearchParams();
  const jumpMessageId = searchParams.get("jumpMessageId");
  const timeoutRefs = useRef({});
  // use to handle scroll
  const messageRef = useRef(null);
  // use to map element uuid
  const messageMapRef = useRef({});

  const { languageMap, user } = useInfoUser();
  const navigate = useNavigate();

  const conversationRef = useRef(null);

  const {
    fakeMessages,
    pushMessage,
    pushMessages,

    pushFiles,
    completeMessage,
    completeFile,
    uploadFileV2,
    createTextMessage,
  } = useSendingContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isJumping, setIsJumping] = useState(false);

  const [searchCacheMention, setSearchCacheMention] = useState(new Map());
  const [isExitGroup, setIsExitGroup] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showAllPin, setShowAllPin] = useState(false);
  const [firstMessageUnread, setFirstMessageUnread] = useState(null);
  const [unreadInDOM, setUnreadInDOM] = useState(0);
  const [isMessageUnread, setIsMessageUnread] = useState(false);
  const [friend, setFriend] = useState(null);

  const [messageList, setMessageList] = useState([]);
  const userListRef = useRef([]);

  const [formData, setFormData] = useState({
    text: "",
    files: [],
    mediaFiles: [],
    encodePhotos: [],
    previewLink: {
      image: null,
      title: null,
      description: null,
      url: null,
    },
  });
  const [quotation, setQuotation] = useState(null);
  const conversationContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [lastMessageTs, setLastMessageTs] = useState(moment());
  const [isMoreMessage, setIsMoreMessage] = useState(false);
  const [isModalAddMember, setIsModalAddMember] = useState(false);
  const [isOpenModalConfirm, setIsOpenModalConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false);
  const [isViewTargetMessage, setIsViewTargetMessage] = useState(false);
  const [isScrollToLastMessage, setIsScrollToLastMessage] = useState(true);
  const [viewTargetMessage, setViewTargetMessage] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [scheduleMessage, setScheduleMessage] = useState(null);
  const [isChatScheduleModal, setIsChatScheduleModal] = useState(false);
  const jumpedRef = useRef(false);
  const updateInitScrollRef = useRef(false);
  const updateFirstScrollRef = useRef(false);
  const abortControllerRef = useRef(new AbortController());
  const observerRef = useRef(null);
  const inputSendRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const previousScrollRef = useRef(0);
  const wasAtBottomMessageRef = useRef(true);
  const [isEnableGroup, setIsEnableGroup] = useState(false);

  /** view mode is PANEL or ARCHIEVE */
  const [viewMode, setViewMode] = useState("PANEL");
  const [archiveType, setArchiveType] = useState("IMAGE"); // "IMAGE" | "FILE" | ...
  const [resourcesArchive, setResourcesArchive] = useState([]);
  const [resources, setResources] = useState({
    images: [],
    files: [],
    linkPreviews: [],
  });

  const conversationListRef = useRef(conversationList);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const onBackToConversations = () => {
    setSelectedConversation(null);
    navigate("/conversation");
  };

  const fakeMessageFileBeforeUpload = (file, currentTime) => {
    return {
      totalVolume: file?.size,
      conversationId: selectedConversation?.conversationId,
      requestUuid: file?.uid,
      memberCount: 0,
      messageId: uuidv4(),
      notificationId: null,
      unreadCount: 0,
      totalChunk: Math.round(file?.size / CHUNK_SIZE),
      senderId: user?.userId,
      senderName: user?.name,
      avatar: user?.avatar,
      files: [
        {
          volume: CHUNK_SIZE,
          originalName: file?.name,
        },
      ],
      shortName: file?.name,
      contentType: "CHUNK",
      username: user?.name,
      isSelf: true,
      progress: 1,
      totalChunkUploaded: 0,
      isSending: true,
      createdAt: currentTime,
      updatedAt: currentTime,
    };
  };

  const clearFormData = () => {
    setFormData({ text: "", files: [], mediaFiles: [], encodePhotos: [] });
    setQuotation(null);
  };

  const isImageFile = (file) => {
    return file && file.type && file.type.startsWith("image/");
  };

  const sendFormData = async (formData) => {
    setIsSending(true);
    clearFormData();
    const allFiles = formData.files || [];
    const imageFiles = allFiles.filter((file) => isImageFile(file));

    await handleSendText(formData, imageFiles);
    await handleSendImage(imageFiles, formData);
    await handleSendFile(formData?.mediaFiles, formData);
    setIsSending(false);
  };

  /*
   * 1. push that one into fake files with status: uploading
   * 2. push to fake message for each file with status: sending
   * 3. push waiting messages to messageList
   * 4. upload one by one file with requestUuid
   * 5. with each file -> upload file chunk step by step
   * 6. with each chunk check if not exist message requestUuid -> that file is canceled -> inversely -> update messageList
   * 7. after finishing every single file -> update file status to sent
   * 8. send message containing that file resourceId in messageRequest
   * 9. receive message from websocket -> clear fake file and message fake
   * */
  const handleSendText = async (formData, imageFiles) => {
    if (
      !formData?.text ||
      formData?.text?.trim() === "" ||
      imageFiles?.length
    ) {
      return;
    }

    let preparedMessages = [];

    for (const content of splitText(formData?.text?.trim()) || []) {
      const mentionIds = extractUserIds(content);
      const requestUuid = uuidv4();

      preparedMessages.push({
        conversationId:
          formData?.conversationId || selectedConversation?.conversationId,
        requestUuid,
        content: sanitizeMentions(content),
        contentType: "TEXT",

        // local message fields
        messageId: requestUuid,
        senderId: user?.userId,
        senderName: user?.name,
        avatar: user?.avatar,
        createdAt: moment().toISOString(),
        isSelf: true,
        status: "PENDING",

        // send payload fields
        token: Cookies.get("access_token"),
        notificationId: formData?.notificationId,
        recipientId: formData?.notificationId ? formData?.recipientId : null,
        quotationId: formData?.quotation?.messageId,
        mentionIds,
        previewLink: formData?.previewLink,
      });
    }

    setMessageList((prev) =>
      sortMessageListDesc([...prev, ...preparedMessages]),
    );
    pushMessages(selectedConversation?.conversationId, preparedMessages);

    for (const preparedMessage of preparedMessages) {
      await apiFactory.messageApi.sendMessage(preparedMessage);
    }
  };

  const handleSendFile = async () => {
    if (!formData?.mediaFiles?.length) return;

    const preparedMessages = formData?.mediaFiles?.map((file, index) => {
      const currentTime = moment().add(index, "seconds").toISOString();
      return fakeMessageFileBeforeUpload(file, currentTime);
    });

    const preparedFiles = formData?.files?.map((file) => ({
      file: file,
      abortController: new AbortController(),
    }));

    setMessageList((prev) =>
      sortMessageListDesc([...prev, ...preparedMessages]),
    );
    pushFiles(preparedFiles);
    pushMessages(selectedConversation?.conversationId, preparedMessages);

    for (const preparedFile of preparedFiles) {
      const message = preparedMessages?.find(
        (preparedMessage) =>
          preparedMessage?.requestUuid === preparedFile?.file?.uid,
      );
      await sendFile(preparedFile, message);
    }
  };

  const handleSendImage = async (imageFiles, formData) => {
    if (!imageFiles?.length) return;

    const { conversationId, text, notificationId, recipientId, quotation } =
      formData;

    const requestUuid = uuidv4();
    const mentionIds = extractUserIds(text);

    const localPhotos = imageFiles.map((file) => ({
      resourceId: uuidv4(),
      pathUrl: URL.createObjectURL(file),
      originalName: file.name,
      volume: file.size,
      isUploading: true,
    }));
    /* image message can contain text*/
    const preparedMessage = {
      conversationId: selectedConversation?.conversationId,
      requestUuid: requestUuid,
      messageId: uuidv4(),
      senderId: user?.userId,
      senderName: user?.name,
      content: text || "",
      contentType: "IMAGE",
      isSending: true,
      photos: localPhotos,
      totalChunk: 0,
      totalChunkUploaded: 0,
      isSelf: true,
      createdAt: moment().toISOString(),
      updatedAt: moment().toISOString(),
      memberCount: 0,
      unreadCount: 0,
      isMention: false,
    };

    setMessageList((prev) => sortMessageListDesc([...prev, preparedMessage]));
    pushMessage(selectedConversation?.conversationId, preparedMessage);

    /* preparedFiles contain: original image and compressed image */
    let preparedFiles = [];
    for (const imageFile of imageFiles) {
      const originalFile = {
        file: imageFile,
        messageRequestUuid: preparedMessage?.requestUuid,
        abortController: new AbortController(),
      };

      preparedFiles.push(originalFile);

      if (
        imageFile?.size > CHUNK_SIZE ||
        imageFile?.width > MAX_DIMENSION ||
        imageFile?.height > MAX_DIMENSION
      ) {
        const compressedFile = await compressImage(imageFile);
        compressedFile.uid = uuidv4();

        preparedFiles.push({
          file: compressedFile,
          messageRequestUuid: preparedMessage?.requestUuid,
          originalUid: originalFile?.file?.uid,
          abortController: new AbortController(),
        });
      }
    }

    pushFiles(preparedFiles);
    createTextMessage(preparedFiles, {
      conversationId: conversationId || selectedConversation?.conversationId,
      requestUuid: requestUuid,
      content: text,
      contentType: "IMAGE",
      notificationId: notificationId,
      recipientId: notificationId ? recipientId : null,
      quotationId: quotation?.messageId,
      mentionIds: mentionIds,
    });
  };

  const sendFile = async (fakeFileMessage, message) => {
    await uploadFileV2(
      fakeFileMessage,
      selectedConversation?.conversationId,
      "CONVERSATION",
      fakeFileMessage?.abortController?.signal,
      (uploadedCount, chunkData) => {},
      null,
      null,
      eventBusType.FILE,
      message,
    );
  };

  const splitText = (inputText, maxChunkLength = MAX_CHUNK_LENGTH) => {
    if (!inputText) return [];

    const lines = inputText.split("\n");
    const resultChunks = [];
    let currentChunk = "";

    for (const line of lines) {
      const potentialLength = currentChunk.length + line.length + 1;

      if (potentialLength <= maxChunkLength) {
        currentChunk += (currentChunk ? "\n" : "") + line;
      } else {
        if (currentChunk) resultChunks.push(currentChunk);
        let remaining = line;
        while (remaining.length > maxChunkLength) {
          resultChunks.push(remaining.substring(0, maxChunkLength));
          remaining = remaining.substring(maxChunkLength);
        }
        currentChunk = remaining;
      }
    }

    if (currentChunk) resultChunks.push(currentChunk);

    return resultChunks;
  };
  const shareMessages = (messageDetail, conversationIds) => {
    try {
      const { requestUuid } = messageDetail;

      const payload = {
        conversationIds: conversationIds,
        requestUuid: requestUuid,
      };

      apiFactory.messageApi.shareMessageByConversationId(payload);
    } catch (error) {
      console.error("Error share messages:", error);
    }
  };

  const removeMessage = (requestUuid) => {
    try {
      const payload = {
        token: Cookies.get("access_token"),
        conversationId: selectedConversation?.conversationId,
        requestUuid,
        contentType: MESSAGE_STATUS.REMOVE_MESSAGE,
      };

      apiFactory.messageApi.removeMessage(payload);

      updateMessageRemove(payload);
    } catch (error) {
      console.error("Error remove message:", error);
    }
  };

  const updateDisableGroup = (message) => {
    if (message?.conversationId !== selectedConversation?.conversationId)
      return;

    selectedConversation.conversationStatus = "INACTIVE";
    setSelectedConversation({ ...selectedConversation });
  };

  const updateEnableGroup = (message) => {
    if (message?.conversationId !== selectedConversation?.conversationId)
      return;

    selectedConversation.conversationStatus = "ACTIVE";
    setSelectedConversation({ ...selectedConversation });
  };

  const updateTyping = (message) => {
    if (message?.conversationId !== selectedConversation?.conversationId)
      return;

    setTypingUsers((prev) => ({
      ...prev,
      [message?.userId]: {
        userName: message?.message,
        typing: true,
      },
    }));

    if (timeoutRefs.current[message?.userId]) {
      clearTimeout(timeoutRefs.current[message?.userId]);
    }

    timeoutRefs.current[message?.userId] = setTimeout(() => {
      setTypingUsers((prev) => {
        const newState = { ...prev };
        delete newState[message?.userId];
        return newState;
      });
    }, 2500);
  };

  const updateMessageListAfterReaction = (message) => {
    if (!message?.requestUuid) return;

    setMessageList((prev) => {
      const index = prev?.findIndex(
        (s) => s?.requestUuid === message?.requestUuid,
      );
      if (index === -1) return prev;

      const next = [...prev];
      next[index] = getUpdatedMessageReaction(prev[index], message);
      return sortMessageListDesc(next);
    });
  };

  const updatePinnedMessage = (basicConversationInfo, pinnedMessageList) => {
    if (
      basicConversationInfo?.conversationId ===
      selectedConversation?.conversationId
    ) {
      setPinnedMessages(pinnedMessageList);
    }

    const conversationListCurrent = conversationListRef?.current || [];

    const conversationId = basicConversationInfo?.conversationId;
    if (!conversationId) return;

    const index = conversationListCurrent?.findIndex(
      (item) => item?.conversationId === conversationId,
    );
    if (index === -1) return;

    const unreadCount =
      !selectedConversation?.conversationId ||
      selectedConversation?.conversationId === conversationId
        ? 0
        : (conversationListCurrent[index]?.unreadCount || 0) + 1;

    const updatedConversation = {
      ...conversationListCurrent[index],
      lastMessageTs: basicConversationInfo?.lastMessageTs,
      unreadCount,
      lastSenderId: basicConversationInfo?.lastSenderId,
      lastMessage: basicConversationInfo?.lastMessage,
      lastMessageType: basicConversationInfo?.lastMessageType,
    };

    const updatedConversationList = [...conversationListCurrent];
    updatedConversationList[index] = updatedConversation;

    handleUpdateConversationList(updatedConversationList);
  };

  const getUpdatedMessage = (item, oldItem) => {
    if (
      item?.senderId !== user?.userId &&
      messageRef?.current &&
      messageRef?.current?.scrollHeight > messageRef?.current?.clientHeight &&
      messageRef.current?.scrollTop + messageRef?.current?.clientHeight <
        messageRef?.current?.scrollHeight
    ) {
      setIsNewMessage(true);
    }

    return {
      ...item,
      username: userListRef?.current?.filter(
        (u) => u?.userId === item?.senderId,
      )?.[0]?.name,
      isSelf: item?.senderId === user?.userId,
      conversationId: selectedConversation?.conversationId,
      photos: item?.photos
        ? processFileUrl(
            item?.photos,
            "/conversation/",
            selectedConversation?.conversationId,
          )
        : item?.photoRequests?.map((s) => s?.contentCode),
      progress: oldItem?.progress !== undefined ? oldItem?.progress + 1 : 0,
      contentType: item?.contentType,
      isSending: oldItem?.progress !== item?.totalChunk - 1,
      totalChunk: item?.totalChunk,
      status: item?.senderId === user?.userId ? "RECEIVED" : item?.status,
    };
  };

  const sortByCreatedAt = (arr) =>
    [...(arr || [])].sort(
      (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
    );

  const applyMessage = (msg) => {
    if (msg?.conversationId !== selectedConversation?.conversationId) return;
    setMessageList((prev) => {
      return sortMessageListDesc(
        upsertByRequestUuid(prev, msg, getUpdatedMessage),
      );
    });
  };

  const applyMessages = (msgs = []) => {
    const shareMsg = msgs.find(
      (msg) => msg?.conversationId === selectedConversation?.conversationId,
    );
    if (!shareMsg) return;
    applyMessage(shareMsg);
  };

  const upsertByRequestUuid = (prev, nextMsg, getUpdatedMessage) => {
    if (!nextMsg?.requestUuid) {
      return prev;
    }

    if (jumpedRef.current) {
      setIsNewMessage(true);
      return prev;
    }

    const idx = prev.findIndex((m) => m?.requestUuid === nextMsg.requestUuid);

    // update
    if (idx !== -1) {
      const updated = getUpdatedMessage(nextMsg, prev[idx]);
      if (!updated) return prev;
      const copy = [...prev];
      copy[idx] = updated;
      return sortByCreatedAt(copy);
    }

    // add
    const updated = getUpdatedMessage(nextMsg);
    if (!updated) return prev;
    return sortByCreatedAt([...prev, updated]);
  };

  const getUpdatedMessageReaction = (current, item) => {
    return {
      ...current,
      ...(item?.respondent !== undefined
        ? { respondent: item.respondent }
        : {}),
      ...(item?.countReactionType !== undefined
        ? { countReactionType: item.countReactionType }
        : {}),
    };
  };

  const updateMessageRemove = (item) => {
    const removingMessage = messageList?.find(
      (s) => s?.requestUuid === item?.requestUuid,
    );

    if (removingMessage) {
      removingMessage.contentType = MESSAGE_STATUS.REMOVE_MESSAGE;
      setMessageList(sortMessageListDesc([...messageList]));
      openNotificationSuccess(
        languageMap?.["success.removeMessage"] ??
          "You removed message successfully",
      );
    }
  };

  const refreshChatMessage = () => {
    setIsMessageSearch(false);
    setPreviousScrollHeight(0);
    setMessageSearch({
      ...messageSearch,
      content: "",
    });
    setIsMenu(false);
  };

  const onCloseChatMessage = () => {
    setIsMessageSearch(false);
    setPreviousScrollHeight(0);

    if (messageSearch?.content?.trim()?.length > 0) {
      setMessageSearch({
        ...messageSearch,
        content: "",
      });
    }

    setIsMenu(false);
  };

  const onSearch = () => {
    if (isMessageSearch) {
      onCloseChatMessage();
    } else {
      setIsMessageSearch(!isMessageSearch);
      setIsMenu(false);
    }
  };

  const openAddMemberModal = () => {
    setIsModalAddMember(true);
    setIsMenu(false);
  };

  const showModalLabel = () => {
    setIsModalLabelGroup(true);
    setIsMenu(false);
  };

  const openModalConfirm = () => {
    setIsOpenModalConfirm(true);
    setIsMenu(false);
  };

  const updateMessageListAfterSeen = (seenMessageTs) => {
    const { conversationId, lastOldSeenTs } = seenMessageTs || {};
    if (!conversationId || !lastOldSeenTs) return;
    if (conversationId !== selectedConversation?.conversationId) return;

    const lastSeenMs = new Date(lastOldSeenTs).getTime();
    if (Number.isNaN(lastSeenMs)) return;

    setMessageList((prev) =>
      (prev || []).map((msg) => {
        // ✅ giữ nguyên shadow hoặc msg không có createdAt
        if (msg?.shadow || !msg?.createdAt) return msg;

        const msgMs = new Date(msg.createdAt).getTime();
        if (Number.isNaN(msgMs)) return msg;

        // msg <= lastOldSeenTs nghĩa là đã seen
        const isSeen = msgMs <= lastSeenMs;

        // tuỳ logic bạn muốn: nếu seen thì giảm unreadCount 1 (đang làm vậy)
        if (!isSeen) return msg;

        const unreadCount = Math.max(msg?.unreadCount || 0, 0);
        if (unreadCount === 0) return msg;

        return { ...msg, unreadCount: unreadCount - 1 };
      }),
    );
  };

  const handleImagePanel = ({ message }) => {
    const formatPhotosToImages = (message) => {
      if (viewMode === "PANEL") {
        return (
          message?.photos?.map((photo) => ({
            id: null,
            messageId: message?.messageId,
            requestUuid: message?.requestUuid,
            senderId: message?.senderId,
            createdAt: message?.createdAt,
            linkPreview: null,
            photos: null,
            media: photo,
          })) ?? []
        );
      } else {
        return {
          id: null,
          messageId: message?.messageId,
          requestUuid: message?.requestUuid,
          senderId: message?.senderId,
          createdAt: message?.createdAt,
          linkPreview: null,
          photos: message?.photos,
          media: null,
        };
      }
    };

    const newImages = formatPhotosToImages(message);

    if (viewMode === "PANEL") {
      setResources((prev) => ({
        ...prev,
        images: [...newImages, ...prev?.images].slice(0, 6),
      }));
    } else if (viewMode === "ARCHIVE" && archiveType === "IMAGE") {
      setResourcesArchive((prev) => {
        if (prev?.[0]?.date === "Recent") {
          const copy = [...prev];
          copy[0] = {
            ...copy[0],
            files: [newImages, ...(copy[0].files || [])],
          };
          return copy;
        }

        return [{ date: "Recent", files: [newImages] }, ...(prev || [])];
      });
    }
  };

  const handleFilePanel = ({ message }) => {
    const formatFileMessage = (message) => {
      const file = message?.files?.[0];

      return {
        id: null,
        messageId: message?.messageId,
        requestUuid: message?.requestUuid,
        senderId: message?.senderId,
        createdAt: message?.createdAt,
        linkPreview: null,
        photos: null,
        media: file,
      };
    };

    if (viewMode === "PANEL") {
      setResources((prev) => ({
        ...prev,
        files: [formatFileMessage(message), ...(prev?.files || [])].slice(0, 3),
      }));
    } else if (viewMode === "ARCHIVE" && archiveType === "FILE") {
      setResourcesArchive((prev) => {
        if (prev?.[0]?.date === "Recent") {
          const copy = [...prev];
          copy[0] = {
            ...copy[0],
            files: [formatFileMessage(message), ...(copy[0].files || [])],
          };
          return copy;
        }
        return [
          { date: "Recent", files: [formatFileMessage(message)] },
          ...(prev || []),
        ];
      });
    }
  };

  const updateUserList = (announcer) => {
    const prev = userListRef.current;
    const index = prev.findIndex((u) => u?.userId === announcer?.userId);

    if (index === -1) {
      userListRef.current = [
        ...prev,
        {
          name: announcer?.userName || "",
          ...announcer,
        },
      ];
    } else {
      const updated = [...prev];
      updated[index] = {
        name: announcer?.userName || prev[index]?.name || "",
        ...announcer,
      };
      userListRef.current = updated;
    }
  };

  const getMember = async () => {
    /** 1. check existing conversation
     *  2. find friend
     */

    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: [user?.userId, selectedPersonalId],
      conversationType: conversationType.PERSONAL,
    });

    if (result?.status === 200 && result?.data?.conversationId) {
      navigate(`/conversation/chat/${result?.data?.conversationId}`);
      return;
    }

    const member = originalOrgList?.find(
      (m) => m.objectId?.toLowerCase() === selectedPersonalId.toLowerCase(),
    );

    setFriend({
      avatar: member?.avatar,
      name: member?.name,
      online: member?.online,
      userId: member?.userCode,
    });
  };

  const handleEventData = (eventData) => {
    if (!eventData) return;

    const {
      seenMessageTs,
      type,
      message,
      messages,
      basicConversationInfo,
      pinnedMessages,
      announcer,
    } = eventData;

    if (announcer) {
      updateUserList(announcer);
    }

    switch (type) {
      case MESSAGE_STATUS.SEEN_MESSAGE:
        updateMessageListAfterSeen(seenMessageTs);
        break;

      case MESSAGE_STATUS.TYPING:
        updateTyping(message);
        break;

      case MESSAGE_STATUS.REACTION_MESSAGE:
        // reaction thường update message hiện có
        updateMessageListAfterReaction(message);
        break;

      case MESSAGE_STATUS.ERROR:
        openNotificationError(message);
        break;

      case MESSAGE_STATUS.SHARE_MESSAGE:
        applyMessages(messages);
        break;

      case MESSAGE_STATUS.PINNED_MESSAGE:
        updatePinnedMessage(basicConversationInfo, pinnedMessages);
        applyMessage(message);
        break;

      case MESSAGE_STATUS.DISABLE_GROUP:
        updateDisableGroup(message);
        applyMessage(message);
        break;

      case MESSAGE_STATUS.ENABLE_GROUP:
        updateEnableGroup(message);
        applyMessage(message);
        break;

      case MESSAGE_STATUS.SCHEDULE:
      case MESSAGE_STATUS.UPDATE_SCHEDULE:
      case MESSAGE_STATUS.DELETE_SCHEDULE: {
        // ✅ không mutate messageList.forEach nữa — update bằng setMessageList
        if (message?.conversationId !== selectedConversation?.conversationId)
          break;

        setMessageList((prev) => {
          const next = prev.map((m) => {
            if (m?.messageScheduleId !== message?.messageScheduleId) return m;

            if (
              type === MESSAGE_STATUS.SCHEDULE &&
              m?.scheduleStatus !== "DELETED"
            ) {
              return { ...m, scheduleStatus: message?.scheduleStatus };
            }
            // UPDATE/DELETE => DELETED
            return { ...m, scheduleStatus: "DELETED" };
          });
          return next;
        });

        applyMessage(message);
        break;
      }

      case MESSAGE_STATUS.IMAGE:
        if (message?.conversationId === selectedConversation?.conversationId) {
          handleImagePanel({ message });
        }
        applyMessage(message);
        break;

      case MESSAGE_STATUS.FILE:
        if (message?.conversationId === selectedConversation?.conversationId) {
          handleFilePanel({ message });
        }
        applyMessage(message);
        break;

      /** when change member -> clear mention list */
      case MESSAGE_STATUS.REMOVE_MESSAGE:
      case MESSAGE_STATUS.NEW_MEMBER:
      case MESSAGE_STATUS.EXIT_GROUP:
        setSearchCacheMention(new Map());
        applyMessage(message);
        break;
      default:
        applyMessage(message);
        break;
    }
  };

  // typing throttle
  const handleTyping = useMemo(
    () =>
      throttle(
        () => {
          if (!selectedConversation?.conversationId) return;

          const payload = {
            event: "TYPING",
            message: JSON.stringify({
              conversationId: selectedConversation.conversationId,
            }),
          };

          try {
            sendChat(JSON.stringify(payload));
          } catch (error) {
            console.error("Error sending typing event:", error);
          }
        },
        REACT_APP_TYPING_TIME,
        { leading: true, trailing: false },
      ),
    [selectedConversation?.conversationId],
  );

  const fetchPinnedMessages = async () => {
    if (selectedConversation?.conversationId) {
      try {
        const response = await apiFactory.messageApi.getPinnedMessage({
          conversationId: selectedConversation?.conversationId,
        });
        if (response?.status === 200) {
          setPinnedMessages(response?.data);
        } else {
          setPinnedMessages([]);
        }
      } catch (error) {
        console.error("Error fetching pinned messages:", error);
      }
    }
  };

  const handleClickButtonIsNewMessage = () => {
    setIsNewMessage(false);
  };

  const getListUserFromMessage = async (messageList) => {
    //get all senderIds and quotation senderIds
    const userIds = [];
    for (const message of messageList) {
      userIds?.push(message?.senderId);
      if (message?.quotation) {
        userIds?.push(message?.quotation?.senderId);
      }
    }

    if (userIds?.length === 0) return;

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: uniq(userIds),
    });

    if (userResult?.status !== 200) {
      return openNotificationError(userResult?.message);
    }

    /** update organizationUser and use it instead of userList in the future */
    updateOrgList(setOriginalOrgList, userResult?.data);

    return userResult?.data;
  };

  const jumpToExistedMessage = (targetMessage) => {
    setIsLoading(true);

    const isLastMessage =
      targetMessage?.messageId ===
      messageList?.[messageList.length - 1]?.messageId;

    if (isLastMessage) {
      setIsLoading(false);
      setViewTargetMessage(targetMessage);
      handleClickButtonIsNewMessage();
      setIsJumping(false);
      return;
    }

    messageMapRef.current[targetMessage?.requestUuid]?.scrollIntoView({
      behavior: "instant",
      block: "center",
      inline: "nearest",
    });

    previousScrollRef.current = messageRef.current.scrollHeight;

    setIsLoading(false);
    setViewTargetMessage(targetMessage);
    setIsScrollToLastMessage(false);
  };

  const jumpToNotExistedMessage = async (targetMessage) => {
    setIsLoading(true);
    const res = await apiFactory.messageApi.getTargetMessage({
      limit: CHATTING.MESSAGE_LIMIT,
      minTs: targetMessage?.createdAt,
      search: { conversationId: selectedConversation?.conversationId },
      direction: "ASCE",
    });

    if (res?.status !== 200) {
      openNotificationError(res?.message);
      return;
    }

    const users = await getListUserFromMessage(res?.data);
    let uniqUsers = userListRef?.current || [];

    if (users?.length) {
      userListRef.current?.push(...users);
      uniqUsers = uniqBy(userListRef.current, "userId");
      userListRef.current = uniqUsers;
    }

    const messList = res?.data?.map((item) => ({
      ...item,
      isSelf: item?.senderId === user?.userId,
      username: uniqUsers?.filter((u) => u?.userId === item?.senderId)?.[0]
        ?.name,
      conversationId: selectedConversation?.conversationId,
      photos: processFileUrl(
        item?.photos,
        "/conversation/",
        selectedConversation?.conversationId,
      ),
      progress: 1,
    }));

    const isLastMessage =
      targetMessage?.messageId === messList?.[messList?.length - 1]?.messageId;

    if (isLastMessage) {
      setIsLoading(false);
      setIsScrollToLastMessage(false);
      setViewTargetMessage(targetMessage);
      handleClickButtonIsNewMessage();
      setIsJumping(false);
      return;
    }

    setIsScrollToLastMessage(true);
    setMessageList([{ shadow: true }, ...messList]);
    setLastMessageTs(messList[0]?.createdAt);
    setIsMoreMessage(true);
    setIsViewTargetMessage(true);
    jumpedRef.current = true;
    delay(() => {
      messageMapRef?.current[targetMessage?.requestUuid]?.scrollIntoView({
        behavior: "instant",
        block: "center",
        inline: "nearest",
      });

      previousScrollRef.current = messageRef.current.scrollHeight;
      setIsViewTargetMessage(true);
      setIsLoading(false);
      setViewTargetMessage(targetMessage);
      setIsScrollToLastMessage(false);
    }, 500);
  };

  const jumpToMessage = useCallback(
    async (targetMessage) => {
      if (!targetMessage) return;
      setIsJumping(true);
      const existedTargetMessageIndex = messageList?.findIndex(
        (message) => message?.messageId === targetMessage?.messageId,
      );

      if (existedTargetMessageIndex > -1) {
        jumpToExistedMessage(targetMessage);
      } else {
        jumpToNotExistedMessage(targetMessage);
      }
      setIsJumping(false);
    },
    [
      messageList,
      selectedConversation?.conversationId,
      userListRef,
      isViewTargetMessage,
      isJumping,
    ],
  );

  const retrieveMessage = async (messageId) => {
    try {
      const res = await apiFactory.messageApi.getMessageById({
        conversationId: selectedConversation?.conversationId,
        messageId,
      });

      if (res?.status !== 200) {
        openNotificationError(res?.message);
        return;
      }

      jumpToNotExistedMessage(res?.data);
    } catch (error) {}
  };

  const initMessageList = async (isChangedConversation = false) => {
    if (!selectedConversation?.conversationId) return;

    setIsLoading(true);
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const result = await apiFactory.messageApi.getList(
        {
          limit: CHATTING.MESSAGE_LIMIT,
          maxTs: null,
          search: {
            conversationId: selectedConversation?.conversationId,
          },
        },
        { signal: abortControllerRef.current.signal },
      );

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      const userList = await getListUserFromMessage(result?.data);
      userListRef.current = userList;

      if (isViewTargetMessage) {
        setIsNewMessage(true);
      }

      let messages = result?.data
        ?.map((item) => ({
          ...item,
          isSelf: item?.senderId === user?.userId,
          username: userList?.filter((u) => u?.userId === item?.senderId)?.[0]
            ?.name,
          conversationId: selectedConversation?.conversationId,
          photos: processFileUrl(
            item?.photos,
            "/conversation/",
            selectedConversation?.conversationId,
          ),
          progress: 1,
        }))
        .reverse();

      messages = [
        ...messages,
        ...(fakeMessages?.[selectedConversation?.conversationId] || []),
      ];

      if (observerRef?.current) {
        observerRef?.current?.disconnect();
      }

      const sortedMessages = [...(messages || [])].sort(
        (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
      );

      setMessageList([{ shadow: true }, ...sortedMessages]);

      setLastMessageTs(messages?.[0]?.createdAt);
      setIsMoreMessage(messages?.length === CHATTING.MESSAGE_LIMIT);
      updateInitScrollRef.current = true;
      setIsLoading(false);
    } catch (error) {
      if (error.name !== "AbortError") {
        return new Error();
      }
    }
  };

  const cancelUpload = (conversationId, requestUuid) => {
    completeFile(requestUuid);
    completeMessage(conversationId, requestUuid);

    setMessageList((prev) => {
      return prev?.filter((message) => message?.requestUuid !== requestUuid);
    });
  };

  useEffect(() => {
    conversationListRef.current = conversationList;
  }, [conversationList]);

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData) return;

      const message = eventData?.message;

      if (
        message?.senderId === user?.userId &&
        message?.conversationId &&
        message?.requestUuid
      ) {
        completeMessage(message?.conversationId, message?.requestUuid);
      }

      // Nếu chưa chọn conversation thì bỏ qua xử lý trong ChatContext
      if (!selectedConversation?.conversationId) return;

      // Nếu muốn ChatContext chỉ xử lý event thuộc conversation đang chọn:
      const msgConversationId = eventData?.message?.conversationId;
      if (
        msgConversationId &&
        msgConversationId !== selectedConversation?.conversationId &&
        // cho phép một số event không có message.conversationId vẫn đi qua
        eventData?.type !== MESSAGE_STATUS.MARK_READ
      ) {
        return;
      }

      handleEventData(eventData);
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const sub = uploadEvent$.subscribe((eventData) => {
      if (!eventData) return;

      if (eventData?.type === "FILE") {
        const payload = eventData?.payload;
        if (payload?.conversationId !== selectedConversation?.conversationId)
          return;

        setMessageList((prev) => {
          const messageIndex = prev?.findIndex(
            (m) => m?.requestUuid === payload?.requestUuid,
          );
          if (prev?.[messageIndex]) {
            prev[messageIndex].totalChunkUploaded =
              (prev[messageIndex].totalChunkUploaded ?? 0) + 1;
          }

          return [...prev];
        });
      } else {
        // IMAGE
      }
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    setFormData({ text: "", files: [], mediaFiles: [], encodePhotos: [] });
  }, [selectedConversation?.conversationId, messageSearch]);

  useEffect(() => {
    setResourcesArchive([]);
  }, [archiveType]);

  useEffect(() => {
    if (selectedConversation) {
      setViewMode("PANEL");
      setIsNewMessage(false);
      setTypingUsers({});
      timeoutRefs.current = {};
      fetchPinnedMessages();
      jumpedRef.current = false;
    }
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    setSearchCacheMention(new Map());

    if (jumpMessageId) {
      retrieveMessage(jumpMessageId);
    } else {
      initMessageList(true);
    }

    inputSendRef?.current?.focus();
    setIsExitGroup(selectedConversation?.role !== role?.HOST);
    setInputValue("");
    previousScrollRef.current = 0;
    updateInitScrollRef.current = false;
    updateFirstScrollRef.current = false;
    setViewTargetMessage(null);
    setShowAllPin(false);
    setFirstMessageUnread(null);
    setIsMessageUnread(false);
    setUnreadInDOM(0);
    setIsScrollToLastMessage(false);
  }, [selectedConversation?.conversationId, jumpMessageId]);

  useEffect(() => {
    if (!selectedPersonalId) return;

    setSelectedConversation({
      type: "PERSONAL",
    });

    getMember();
    setMessageList([
      { shadow: true },
      {
        contentType: "CREATE_PERSONAL",
        createdAt: new Date().toISOString(),
        requestUuid: uuidv4(),
      },
    ]);
  }, [selectedPersonalId, originalOrgList]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const values = useMemo(
    () => ({
      userListRef,
      isModalAddMember,
      isOpenModalConfirm,
      loading,
      isMoreMessage,
      isNewMessage,
      conversationContainerRef,
      conversationList,
      messageList,
      selectedConversation,
      quotation,
      formData,
      lastMessageTs,
      isViewTargetMessage,
      viewTargetMessage,
      sendFormData,
      setFormData,
      setMessageList,
      handleUpdateConversationList,
      setSelectedConversation,
      setQuotation,
      setLoading,
      setLastMessageTs,
      setIsMoreMessage,
      conversationRef,
      refreshChatMessage,
      setIsModalAddMember,
      setIsOpenModalConfirm,
      onSearch,
      onBackToConversations,
      isSending,
      setIsSending,
      openAddMemberModal,
      showModalLabel,
      openModalConfirm,
      setActiveKey,
      activeKey,
      setIsNewMessage,
      setIsViewTargetMessage,
      setViewTargetMessage,
      shareMessages,
      removeMessage,
      previousScrollRef,
      onCloseChatMessage,
      messageRef,
      isScrollToLastMessage,
      setIsScrollToLastMessage,
      handleTyping,
      typingUsers,
      pinnedMessages,
      isMobile,
      setIsEnableGroup,
      isEnableGroup,
      scheduleMessage,
      setScheduleMessage,
      isChatScheduleModal,
      setIsChatScheduleModal,
      resources,
      setResources,
      viewMode,
      setViewMode,
      resourcesArchive,
      setResourcesArchive,
      archiveType,
      setArchiveType,
      fetchPinnedMessages,
      wasAtBottomMessageRef,
      messageMapRef,
      jumpedRef,
      updateInitScrollRef,
      updateFirstScrollRef,
      isLoading,
      setIsLoading,
      jumpToMessage,
      isJumping,
      setIsJumping,
      getListUserFromMessage,
      searchCacheMention,
      setSearchCacheMention,
      isExitGroup,
      setIsExitGroup,
      inputValue,
      setInputValue,
      showAllPin,
      setShowAllPin,
      firstMessageUnread,
      setFirstMessageUnread,
      unreadInDOM,
      setUnreadInDOM,
      isMessageUnread,
      setIsMessageUnread,
      initMessageList,
      friend,
      cancelUpload,
    }),
    [
      userListRef,
      messageList,
      formData,
      isModalAddMember,
      isOpenModalConfirm,
      lastMessageTs,
      isSending,
      isNewMessage,
      activeKey,
      quotation,
      isViewTargetMessage,
      viewTargetMessage,
      typingUsers,
      pinnedMessages,
      isMobile,
      isEnableGroup,
      scheduleMessage,
      isChatScheduleModal,
      resources,
      viewMode,
      resourcesArchive,
      archiveType,
      conversationList,
      selectedConversation,
      loading,
      isMoreMessage,
      wasAtBottomMessageRef,
      messageMapRef,
      jumpedRef,
      updateInitScrollRef,
      updateFirstScrollRef,
      isScrollToLastMessage,
      isLoading,
      isJumping,
      searchCacheMention,
      isExitGroup,
      inputValue,
      showAllPin,
      firstMessageUnread,
      unreadInDOM,
      isMessageUnread,
      friend,
    ],
  );

  return <ChatContext.Provider value={values}>{children}</ChatContext.Provider>;
};
